import { useState } from "react";
import dayjs from "dayjs";

import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Card from "react-bootstrap/Card";
import { FaDatabase } from "react-icons/fa";

import { getQuarterDates } from "@/utils/quarters";
import { exportExcel } from "@/utils/exports";
import { getAge } from "@/utils/dateUtil";
import { getCitizenScoreStatusDescriptionIndicatorC2 } from "@/utils/indicatorsEsf";
import { Paginated } from "@/@types/generics/paginated";
import { GenericObject } from "@/@types/generics/genericals";
import { DetailedCitizenScoresIndicatorC2Data } from "@/services/esus/indicator/esf/c2/getDetailedCitizensScoresIndicatorC2";

import { IndicatorEsfC2CitizensScoresFilters } from "./IndicatorEsfC2CitizensScoresFilters";
import { IndicatorEsfC2CitizensScoresTable } from "./IndicatorEsfC2CitizensScoresTable";
import { PaginationLinks } from "@/components/PaginationLinks";
import { NoRecordsFeedback } from "@/components/NoRecordsFeedback";
import { LoadingScreen } from "@/components/LoadingScreen";

type IndicatorEsfC2CitizensScoresProps = {
	selectedQuarter: string;
	search: string;
	setSearch: (search: string) => void;
	isEvaluated: boolean | undefined;
	setIsEvaluated: (isEvaluated: boolean | undefined) => void;
	isLoading: boolean;
	paginatedCitizensScores: Paginated<DetailedCitizenScoresIndicatorC2Data>;
	pageNumber: number;
	setPageNumber: (pageNumber: number) => void;
	handleGetAllDetailedCitizensScoresIndicatorC2: () => Promise<
		DetailedCitizenScoresIndicatorC2Data[]
	>;
};

export function IndicatorEsfC2CitizensScores({
	selectedQuarter,
	search,
	setSearch,
	isEvaluated,
	setIsEvaluated,
	isLoading,
	paginatedCitizensScores,
	pageNumber,
	setPageNumber,
	handleGetAllDetailedCitizensScoresIndicatorC2,
}: Readonly<IndicatorEsfC2CitizensScoresProps>) {
	const [isLoadingExportData, setIsLoadingExportData] = useState(false);

	async function handleExportTable() {
		setIsLoadingExportData(true);
		const citizensScores = await handleGetAllDetailedCitizensScoresIndicatorC2();
		setIsLoadingExportData(false);
		const quarterDates = getQuarterDates(selectedQuarter);

		if (Object.keys(citizensScores).length > 0) {
			const excelData: GenericObject[] =
				transformIndicatorC2CitizensScoresToGenericObjectArray(citizensScores);
			exportExcel(
				excelData,
				`Pontuações de cidadãos - ${dayjs(quarterDates.startDate).format(
					"DD-MM-YYYY"
				)} à ${dayjs(quarterDates.endDate).format("DD/MM/YYYY")}`
			);
		}
	}

	function transformIndicatorC2CitizensScoresToGenericObjectArray(
		citizensScores: DetailedCitizenScoresIndicatorC2Data[]
	) {
		return citizensScores.map(
			(citizenScores) =>
				({
					Cidadão: citizenScores.fullName,
					"Data de Nascimento": dayjs(citizenScores.birthdate).format("DD/MM/YYYY"),
					Idade: getAge(citizenScores.birthdate),
					Area: citizenScores.area ?? "FA",
					"Ine Equipe": citizenScores.healthTeam?.ine ?? "",
					"Nome Equipe": citizenScores.healthTeam?.name ?? "",
					"1ª Consulta": getCitizenScoreStatusDescriptionIndicatorC2(
						citizenScores.firstAttendanceStatus
					),
					Consultas: getCitizenScoreStatusDescriptionIndicatorC2(
						citizenScores.nineAttendancesStatus
					),
					"Peso/Altura": getCitizenScoreStatusDescriptionIndicatorC2(
						citizenScores.nineWeightAndHeightMetricsRegistersStatus
					),
					Visitas: getCitizenScoreStatusDescriptionIndicatorC2(
						citizenScores.twoHomeVisitsStatus
					),
					Vacinações: getCitizenScoreStatusDescriptionIndicatorC2(
						citizenScores.vaccinationsStatus
					),
					Pontos: citizenScores.score,
				} as GenericObject)
		);
	}

	return (
		<Row className="bg-white m-3 p-2">
			<Col>
				<LoadingScreen loading={isLoadingExportData} />
				<Card>
					<Card.Header>
						<IndicatorEsfC2CitizensScoresFilters
							search={search}
							setSearch={setSearch}
							isEvaluated={isEvaluated}
							setIsEvaluated={setIsEvaluated}
							handleExportTable={handleExportTable}
						/>
					</Card.Header>
					<Card.Body>
						<IndicatorEsfC2CitizensScoresTable
							citizensScores={paginatedCitizensScores.data}
							isLoading={isLoading}
						/>
					</Card.Body>
					<Card.Footer>
						{paginatedCitizensScores?.totalRecords ? (
							<PaginationLinks
								itemsPerPage={paginatedCitizensScores.pageSize}
								totalPages={paginatedCitizensScores.totalRecords}
								changeSelectedPage={(pageNumber) => {
									setPageNumber(pageNumber);
								}}
								pageNumber={pageNumber || 0}
							/>
						) : (
							!isLoading && (
								<NoRecordsFeedback
									message={"Nenhum resultado encontrado."}
									icon={<FaDatabase />}
								/>
							)
						)}
					</Card.Footer>
				</Card>
			</Col>
		</Row>
	);
}
